/**
 * @file SectionContentTab.tsx
 * @description Вкладка «Содержимое» секции (phase19.16 Р2; uiux-design-r2
 * §2, §4): шапка раздела, строка «2 колонки · 1:1 — изменить ›» (число
 * колонок меняется на вкладке «Оформление»), сетка колонок с элементами.
 *
 * SB122 (uiux-settings-groups §5 шаг 4): две группы-карточки
 * `SettingsGroups panel="section-content"` — «Шапка раздела» (сводка —
 * заголовок раздела или «без заголовка») и «Колонки и элементы» (сводка
 * «3 колонки · 7 элементов»). Оглавления нет (меньше 4 групп).
 * Ревью партии SB122: группа с замечанием — ✕ в заголовке; неудачное
 * «Сохранить блок» раскрывает первую такую (`RevealIssueGroup`).
 */

import type { SectionPayload } from "@chaiberi/shared";

import { JUMP_LINK_CLASS } from "../../ui/jump-link";
import { SettingsGroup, SettingsGroups } from "../../ui/settings-groups";
import { SectionHeaderFields } from "./content/SectionHeaderFields";
import { SectionGrid } from "./content/SectionGrid";
import { RevealIssueGroup } from "./RevealIssueGroup";
import { useSectionIssueGroups } from "../../../hooks/site-builder/useSectionIssueGroups";
import { CONTENT_ISSUE_RULES } from "../../../lib/site-builder/issue-groups";
import { gridSummary, headerSummary } from "../../../lib/site-builder/content-summary";
import { columnsLabel } from "../../../lib/site-builder/section-meta";
import { sharesLabel } from "../../../lib/site-builder/column-shares";
import { sectionSurfaceBg } from "../../../lib/site-builder/text-style";
import type { OtherH1 } from "../../../lib/site-builder/page-h1";

/** Ключ панели групп: память `commora.settings-groups.section-content`. */
export const SECTION_CONTENT_PANEL = "section-content";

interface Props {
  payload: SectionPayload;
  onChange: (payload: SectionPayload) => void;
  selectedId: string | null;
  onSelect: (elementId: string | null, fresh?: boolean) => void;
  /** «изменить ›» — к группе «Колонки» на вкладке «Оформление». */
  onEditColumns: () => void;
  otherH1ForHeader: OtherH1 | null;
}

export function SectionContentTab({ payload, onChange, selectedId, onSelect, onEditColumns, otherH1ForHeader }: Props) {
  const count = payload.columns.items.length;
  const bad = useSectionIssueGroups(CONTENT_ISSUE_RULES);
  const status = (id: (typeof CONTENT_ISSUE_RULES)[number]["group"]) => (bad.has(id) ? ("error" as const) : undefined);
  return (
    <SettingsGroups panel={SECTION_CONTENT_PANEL}>
      <RevealIssueGroup rules={CONTENT_ISSUE_RULES} />
      <SettingsGroup id="header" title="Шапка раздела" summary={headerSummary(payload.header)} hint="Заголовок и ссылка «Смотреть все» над колонками." status={status("header")}>
        <SectionHeaderFields header={payload.header} bg={sectionSurfaceBg(payload.look)} otherH1={otherH1ForHeader} onChange={(header) => onChange({ ...payload, header })} />
      </SettingsGroup>
      <SettingsGroup id="grid" title="Колонки и элементы" summary={gridSummary(payload.columns)} status={status("grid")}>
        <p className="type-caption flex flex-wrap items-center gap-x-2 text-on-surface-variant">
          {columnsLabel(count)}
          {count > 1 ? ` · ${sharesLabel(payload.columns.shares)}` : ""}
          <button type="button" className={JUMP_LINK_CLASS} onClick={onEditColumns}>
            — изменить ›
          </button>
        </p>
        <SectionGrid payload={payload} onChange={onChange} selectedId={selectedId} onSelect={onSelect} />
      </SettingsGroup>
    </SettingsGroups>
  );
}
